import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { TableName } from '../../constants/table';
import {
  getByPartitionKey,
  deleteEntity,
} from '../../lib/services/api-service';

interface CommonCodeMapping {
  PartitionKey: string;
  RowKey: string;
  CommonCodeId: string;
  CommonCodeOCR: string;
  CommonCodeName?: string;
}

interface VendorMappingsDialogProps {
  open: boolean;
  onClose: () => void;
  vendorId: string;
  vendorName: string;
  originalOCR: string;
  tenantId: string;
  onMappingsChanged: () => void;
}

const VendorMappingsDialog: React.FC<VendorMappingsDialogProps> = ({
  open,
  onClose,
  vendorId,
  vendorName,
  originalOCR,
  tenantId,
  onMappingsChanged,
}) => {
  const [mappings, setMappings] = useState<CommonCodeMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const partitionKey = tenantId.toLowerCase();
      const allMappings = await getByPartitionKey(
        TableName.CommonCodeMapping,
        partitionKey,
      );

      const vendorMappings = allMappings.filter(
        (m: CommonCodeMapping) => m.CommonCodeId === vendorId,
      );

      setMappings(vendorMappings);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      toast.error('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && vendorId) {
      fetchMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendorId]);

  // Find the primary mapping (matches originalOCR) and other mappings
  const { primaryMapping, otherMappings } = useMemo(() => {
    const primary = mappings.find(m => m.CommonCodeOCR === originalOCR);
    const others = mappings.filter(m => m.CommonCodeOCR !== originalOCR);
    return { primaryMapping: primary, otherMappings: others };
  }, [mappings, originalOCR]);

  const handleDeleteMapping = async (mapping: CommonCodeMapping) => {
    setDeleting(mapping.RowKey);
    try {
      await deleteEntity(
        TableName.CommonCodeMapping,
        mapping.PartitionKey,
        mapping.RowKey,
      );
      toast.success(`Deleted mapping: ${mapping.CommonCodeOCR}`);
      fetchMappings();
      onMappingsChanged();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error('Failed to delete mapping');
    } finally {
      setDeleting(null);
    }
  };

  // Total count includes primary + other mappings
  const totalMappingsCount = (primaryMapping ? 1 : 0) + otherMappings.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div>
          Mappings for <strong>{vendorName}</strong>
        </div>
        <div style={{ fontSize: '0.875rem', color: 'gray' }}>
          {totalMappingsCount} mapping(s) found
        </div>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <CircularProgress />
          </div>
        ) : mappings.length === 0 ? (
          <div className="py-4 text-center text-gray-500">
            No mappings found for this vendor.
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Vendor OCR</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* First row: The primary vendor mapping (not deletable) */}
                {primaryMapping && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>
                      {primaryMapping.CommonCodeOCR}
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: '0.75rem',
                          color: 'gray',
                        }}>
                        (Primary)
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Primary vendor mapping cannot be deleted">
                        <span>
                          <IconButton size="small" color="default" disabled>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )}
                {/* Other mappings (deletable) */}
                {otherMappings.map(mapping => (
                  <TableRow key={mapping.RowKey}>
                    <TableCell>{mapping.CommonCodeOCR}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete mapping">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleting === mapping.RowKey}
                            onClick={() => handleDeleteMapping(mapping)}>
                            {deleting === mapping.RowKey ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorMappingsDialog;
